import type { Locale } from "@/lib/i18n";

export type BlogLabels = {
  eyebrow: string;
  listTitle: string;
  listDescription: string;
  empty: string;
  featured: string;
  readMore: string;
  by: string;
  views: (n: number) => string;
  prev: string;
  next: string;
  pageOf: (page: number, pages: number) => string;
  breadcrumbHome: string;
  breadcrumbBlog: string;
  toc: string;
  related: string;
  share: string;
  copyLink: string;
  copied: string;
  authorBoxTitle: string;
  authorBoxTeamBio: string;
  authorBoxPersonBio: (name: string) => string;
  updatedAt: string;
  disclaimerTitle: string;
  disclaimer: string;
};

const TR: BlogLabels = {
  eyebrow: "Akademi",
  listTitle: "Oravity Akademi",
  listDescription:
    "Klinik yönetimi, dijital sağlık ve pratik iş akışları üzerine rehberler, ipuçları ve güncellemeler.",
  empty: "Henüz yayınlanmış yazı yok.",
  featured: "Öne çıkan",
  readMore: "Yazıyı oku",
  by: "Yazan",
  views: (n) => `${n.toLocaleString("tr-TR")} görüntülenme`,
  prev: "Önceki",
  next: "Sonraki",
  pageOf: (page, pages) => `Sayfa ${page} / ${pages}`,
  breadcrumbHome: "Ana Sayfa",
  breadcrumbBlog: "Blog",
  toc: "İçindekiler",
  related: "İlgili yazılar",
  share: "Paylaş",
  copyLink: "Bağlantıyı kopyala",
  copied: "Kopyalandı",
  authorBoxTitle: "Oravity Uzman Ekibi",
  authorBoxTeamBio:
    "Klinik yönetim yazılımı alanında uzmanlaşmış içerik ve ürün ekibimiz; sahadaki deneyim ve sektörel bilgiyle bu yazıları hazırlar.",
  authorBoxPersonBio: (name) =>
    `${name}, Oravity bünyesinde klinik yönetimi ve dijital sağlık konularında içerik üretir.`,
  updatedAt: "Güncellenme",
  disclaimerTitle: "Not:",
  disclaimer:
    "Bu yazı genel bilgilendirme amaçlıdır; hukuki, mali veya tıbbi danışmanlık yerine geçmez. Mevzuat, resmî tarifeler ve kamu sistemlerine ilişkin süreçler değişebilir; uygulamadan önce Sağlık Bakanlığı, TDB ve ilgili kurumların güncel duyurularını kontrol edin.",
};

const EN: BlogLabels = {
  eyebrow: "Academy",
  listTitle: "Oravity Academy",
  listDescription:
    "Guides, tips and updates on clinic management, digital health and practical workflows.",
  empty: "No published posts yet.",
  featured: "Featured",
  readMore: "Read article",
  by: "By",
  views: (n) => `${n.toLocaleString("en-US")} views`,
  prev: "Previous",
  next: "Next",
  pageOf: (page, pages) => `Page ${page} of ${pages}`,
  breadcrumbHome: "Home",
  breadcrumbBlog: "Blog",
  toc: "Table of contents",
  related: "Related posts",
  share: "Share",
  copyLink: "Copy link",
  copied: "Copied",
  authorBoxTitle: "Oravity Expert Team",
  authorBoxTeamBio:
    "Our content and product team, specialised in clinic management software, writes these articles from field experience and industry expertise.",
  authorBoxPersonBio: (name) =>
    `${name} writes about clinic management and digital health at Oravity.`,
  updatedAt: "Updated",
  disclaimerTitle: "Note:",
  disclaimer:
    "This article is for general information only and is not legal, financial or medical advice. Regulations, official fee schedules and public-system procedures change; check the latest announcements from the Ministry of Health, the Turkish Dental Association and other relevant authorities before acting.",
};

export function blogLabels(locale: Locale): BlogLabels {
  return locale === "en" ? EN : TR;
}

/** Liste sayfası yolu: /akademi veya /en/academy */
export function blogListPath(locale: Locale): string {
  return locale === "en" ? "/en/academy" : "/akademi";
}

/** Tarih biçimi (locale'e göre). */
export function formatBlogDate(
  value: string | null | undefined,
  locale: Locale
): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat(locale === "tr" ? "tr-TR" : "en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(d);
}
